import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import LocalPhoneOutlined from '@mui/icons-material/LocalPhoneOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type {
  EncounterEnvelopePayload,
  TimelineEnvelope,
} from '../../types';

import { formatDateRange } from './helpers';

interface EncounterCardProps {
  envelope: TimelineEnvelope;
  /** Children rendered nested below — the encounter card frames its thread. */
  children?: React.ReactNode;
}

const MODALITY_ICON: Record<string, React.ComponentType<{ sx?: object }>> = {
  in_person: PersonOutlined,
  video: VideocamOutlined,
  audio: LocalPhoneOutlined,
};

const MODALITY_LABEL: Record<string, string> = {
  in_person: 'In person',
  video: 'Video',
  audio: 'Audio',
};

/**
 * Encounter envelope card. Always a thread root — children (DocRefs,
 * payments, communications) render beneath it via the `children` slot.
 *
 * Visually distinct from doc cards: the encounter is the *event*; the
 * docs are *artifacts of that event*.
 */
export function EncounterCard({ envelope, children }: EncounterCardProps) {
  const payload = (envelope.payload ?? {}) as EncounterEnvelopePayload;
  const modality = payload.modality ?? 'in_person';
  const Icon = MODALITY_ICON[modality] ?? EventNoteOutlined;
  const modalityLabel = MODALITY_LABEL[modality] ?? modality;
  const noteStatus = payload.note_status;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                {payload.title || 'Encounter'}
              </Typography>
              {noteStatus && (
                <Chip
                  size="small"
                  label={noteStatus === 'signed' ? 'Note signed' : 'Note pending'}
                  variant="soft"
                  color={noteStatus === 'signed' ? 'success' : 'warning'}
                />
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              divider={<MetaDot />}
              sx={{ mt: 0.25, color: 'text.secondary' }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {modalityLabel}
              </Typography>
              {payload.provider && (
                <Typography variant="caption">{payload.provider}</Typography>
              )}
              {payload.facility && (
                <Typography variant="caption">{payload.facility}</Typography>
              )}
              {envelope.effective_date && (
                <Typography variant="caption">
                  {formatDateRange(envelope.effective_date, envelope.effective_date)}
                </Typography>
              )}
              {payload.duration_min != null && (
                <Typography variant="caption">{payload.duration_min} min</Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      {children && (
        <Box
          sx={{
            borderTop: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            p: 1,
          }}
        >
          <Stack spacing={1}>{children}</Stack>
        </Box>
      )}
    </Paper>
  );
}

function MetaDot() {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ color: 'text.disabled', mx: 0.5 }}
      aria-hidden
    >
      ·
    </Typography>
  );
}
